import React, { useState, useEffect, useMemo } from "react";
import { HiOutlineEmojiSad, HiOutlineFilter, HiOutlineViewGrid, HiOutlineViewList, HiCheckCircle, HiCloudUpload, HiOutlineShare, HiX, HiDownload } from "react-icons/hi";
import { AnimatePresence, motion } from "framer-motion";
import FileCard from "../Components/FileCard";
import FolderCard from "../Components/FolderCard";
import ViewFileModal from "../Components/ViewFileModal";
import ShareModal from "../Components/ShareModal";
import RenameModal from "../Components/RenameModal";
import DownloadConfirmModal from "../Components/DownloadConfirmModal";
import {
  getSharedWithMeAPI,
  getFolderContentAPI,
  grantAccessAPI,
  renameFileAPI,
  renameFolderAPI,
  requestDownloadAPI,
  getDownloadUrl
} from "../services/driveService";
import { toast } from "react-toastify";
import useAuth from "../useAuth";

export default function SharedPage() {
  const [viewSize, setViewSize] = useState(() => {
    const saved = localStorage.getItem("drive_viewSize");
    return (saved === "big" ? "small" : saved) || "small";
  });
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [viewingFile, setViewingFile] = useState(null);
  const [sharingItem, setSharingItem] = useState(null);
  const [renamingItem, setRenamingItem] = useState(null);
  const [downloadItem, setDownloadItem] = useState(null);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [sortBy, setSortBy] = useState(() => localStorage.getItem("shared_sortBy") || "name");
  const [sortOrder, setSortOrder] = useState(() => localStorage.getItem("shared_sortOrder") || "asc");

  useEffect(() => {
    localStorage.setItem("shared_sortBy", sortBy);
    localStorage.setItem("shared_sortOrder", sortOrder);
    fetchData();
  }, [currentFolderId, sortBy, sortOrder]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [breadcrumbs, setBreadcrumbs] = useState([{ id: null, name: "Shared with me" }]);
  const { user } = useAuth();

  const totalSelectedSize = useMemo(() => {
    let total = 0;
    selectedItems.forEach(key => {
      const [type, id] = key.split(':');
      if (type === 'folder') {
        const folder = folders.find(f => String(f.id) === String(id));
        if (folder) total += (folder.size || 0);
      } else {
        const file = files.find(f => String(f.id) === String(id));
        if (file) total += (file.size || 0);
      }
    });
    return total;
  }, [selectedItems, folders, files]);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 B";
    if (!bytes) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };



  const fetchData = async () => {
    setLoadingData(true);
    try {
      if (currentFolderId === null) {
        console.log("Fetching shared with me content with sort:", sortBy, sortOrder);
        const data = await getSharedWithMeAPI(sortBy, sortOrder);
        setFolders(data?.folders || []);
        setFiles(data?.files || []);
      } else {
        console.log("Fetching folder content with sort:", sortBy, sortOrder);
        const data = await getFolderContentAPI(currentFolderId, sortBy, sortOrder);
        setFolders(data?.folders || []);
        setFiles(data?.files || []);
      }
    } catch (error) {
      toast.error("Failed to load content");
      console.error(error);
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    // Initialize history state on load if empty
    if (!window.history.state || !window.history.state.breadcrumbs) {
      window.history.replaceState({ breadcrumbs, folderId: currentFolderId }, "");
    }

    const handlePopState = (event) => {
      if (event.state && event.state.breadcrumbs) {
        setCurrentFolderId(event.state.folderId);
        setBreadcrumbs(event.state.breadcrumbs);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const handleFolderClick = (folder) => {
    const newBreadcrumbs = [...breadcrumbs, { id: folder.id, name: folder.name }];
    window.history.pushState({ breadcrumbs: newBreadcrumbs, folderId: folder.id }, "");
    setBreadcrumbs(newBreadcrumbs);
    setCurrentFolderId(folder.id);
  };

  const handleBreadcrumbClick = (id, index) => {
    const newBreadcrumbs = breadcrumbs.slice(0, index + 1);
    window.history.pushState({ breadcrumbs: newBreadcrumbs, folderId: id }, "");
    setBreadcrumbs(newBreadcrumbs);
    setCurrentFolderId(id);
  };

  const handleToggleSelect = (item, type) => {
    const itemKey = `${type}:${item.id}`;
    setSelectedItems(prev =>
      prev.includes(itemKey)
        ? prev.filter(k => k !== itemKey)
        : [...prev, itemKey]
    );
  };

  // const handleShareSubmit = async (userId, userIdentifier, permission) => {
  //   if (!sharingItem) return;
  //   try {
  //     const resourceType = sharingItem.type;
  //     await grantAccessAPI(userId, userIdentifier, sharingItem.id, resourceType, permission);
  //     toast.success(`Access granted for ${sharingItem.name}`);
  //     setSharingItem(null);
  //   } catch (error) {
  //     toast.error(error?.message || "Failed to grant access");
  //   }
  // };

  const handleRenameSubmit = async (newName) => {
    if (!renamingItem) return;
    try {
      if (renamingItem.type === "file") {
        await renameFileAPI(renamingItem.id, newName);
      } else {
        await renameFolderAPI(renamingItem.id, newName);
      }
      toast.success("Item renamed successfully");
      fetchData();
      setRenamingItem(null);
    } catch (error) {
      toast.error(error?.message || "Failed to rename item");
    }
  };


  const handleBatchDownload = async () => {
    const selectedFiles = files.filter(f => selectedItems.includes(`file:${f.id}`));
    const selectedFoldersCount = selectedItems.filter(k => k.startsWith('folder:')).length;

    if (selectedFiles.length === 0) {
      if (selectedFoldersCount > 0) {
        toast.info("Only files can be downloaded directly. Folders cannot be downloaded.");
      }
      return;
    }

    if (selectedFoldersCount > 0) {
      toast.warning(`${selectedFoldersCount} folders were skipped. Only files can be downloaded directly.`);
    }

    toast.info(`Preparing ${selectedFiles.length} files for download...`);
    const token = localStorage.getItem("dbToken");

    for (const file of selectedFiles) {
      try {
        const requestData = await requestDownloadAPI(file.id);
        const tempGuid = requestData?.tempFileGuid || requestData?.data?.tempFileGuid;
        if (tempGuid) {
          const finalUrl = `${getDownloadUrl(tempGuid)}?token=${token}`;
          const link = document.createElement("a");
          link.href = finalUrl;
          link.download = file.name;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } catch (err) {
        console.error("Batch download failed for", file.name, err);
      }
      // Small delay to prevent browser download protection from blocking multiple popups
      await new Promise(r => setTimeout(r, 1000));
    }
    setSelectedItems([]);
  };

  const handleBatchShare = () => {
    const itemsToShare = selectedItems.map(key => {
      const [type, id] = key.split(':');
      const item = type === 'folder'
        ? folders.find(f => String(f.id) === String(id))
        : files.find(f => String(f.id) === String(id));
      return { ...item, type };
    }).filter(item => item.id);

    if (itemsToShare.length > 0) {
      setSharingItem(itemsToShare);
    }
  };

  const handleShareSubmit = async (userIds, resources, permission) => {
    try {
      await grantAccessAPI(userIds, resources, permission);
      toast.success(`Access granted successfully`);
      setSharingItem(null);
      setSelectedItems([]);
    } catch (error) {
      toast.error(error?.message || "Failed to grant access");
    }
  };

  return (
    <div className="space-y-8 pb-20">
      <div className="flex flex-col gap-4 mb-6 md:mb-10">
        <div className="flex items-center gap-2 bg-white px-3 md:px-6 py-2 md:py-3 rounded-2xl border border-slate-200 w-full md:w-fit shadow-xl overflow-x-auto no-scrollbar">
          {breadcrumbs.map((crumb, i) => (
            <React.Fragment key={crumb.id || 'root'}>
              <button
                onClick={() => handleBreadcrumbClick(crumb.id, i)}
                className={`text-[9px] md:text-[10px] font-bold uppercase tracking-widest transition-all whitespace-nowrap ${i === breadcrumbs.length - 1
                  ? "text-indigo-600"
                  : "text-slate-400 hover:text-slate-900"
                  }`}
              >
                {crumb.name}
              </button>
              {i < breadcrumbs.length - 1 && <span className="text-slate-300 font-bold">/</span>}
            </React.Fragment>
          ))}
        </div>
        <h1 className="text-2xl md:text-4xl font-bold text-slate-900 tracking-tight">
          {breadcrumbs[breadcrumbs.length - 1].name}
        </h1>
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-[0.2em]">Shared Items</h3>
        <div className="flex items-center gap-4 w-full sm:w-auto justify-between sm:justify-end">
          <AnimatePresence>
            {selectedItems.length > 0 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="flex items-center gap-1 bg-indigo-600 p-1 rounded-xl shadow-lg shadow-indigo-100"
              >
                <div className="px-3 py-1 flex items-center gap-2 border-r border-white/20">
                  <HiCheckCircle className="text-white" size={16} />
                  <div className="flex flex-col leading-tight">
                    <span className="text-[10px] font-black text-white uppercase tracking-widest">{selectedItems.length} SELECTED</span>
                    <span className="text-[8px] font-bold text-indigo-200 uppercase tracking-tighter">{formatFileSize(totalSelectedSize)}</span>
                  </div>
                </div>
                <button
                  onClick={handleBatchDownload}
                  className="p-2 text-white hover:bg-white/10 rounded-lg transition-all"
                  title="Download Selected"
                >
                  <HiDownload size={18} />
                </button>
                <button
                  onClick={handleBatchShare}
                  className="p-2 text-white hover:bg-white/10 rounded-lg transition-all"
                  title="Share Selected"
                >
                  <HiOutlineShare size={18} />
                </button>
                <button
                  onClick={() => setSelectedItems([])}
                  className="p-2 text-white hover:bg-white/10 rounded-lg transition-all border-l border-white/20"
                  title="Clear Selection"
                >
                  <HiX size={16} />
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex items-center gap-1 bg-slate-100/80 p-1 rounded-xl border border-slate-200/60 shadow-sm backdrop-blur-sm">
            <div className="flex items-center pl-2.5 pr-1 border-r border-slate-200/80 gap-2">
              {loadingData ? (
                <div className="w-3.5 h-3.5 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
              ) : (
                <HiOutlineFilter size={13} className="text-slate-400" />
              )}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="bg-transparent text-[10px] font-black uppercase tracking-wider focus:outline-none cursor-pointer text-slate-700 hover:text-indigo-600 transition-colors appearance-none pr-4 relative"
                style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'%2394a3b8\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\'%3E%3C/path%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right center', backgroundSize: '10px' }}
              >
                <option value="name">Name</option>
                <option value="date">Date</option>
                <option value="size">Size</option>
                <option value="type">Type</option>
              </select>
            </div>
            <div className="px-2">
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="bg-transparent text-[10px] font-black uppercase tracking-wider focus:outline-none cursor-pointer text-slate-700 hover:text-indigo-600 transition-colors appearance-none pr-4"
                style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'%2394a3b8\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\'%3E%3C/path%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right center', backgroundSize: '10px' }}
              >
                <option value="asc">ASC</option>
                <option value="desc">DESC</option>
              </select>
            </div>
          </div>
          <div className="flex bg-slate-100 p-1 rounded-xl border border-slate-200">
            <button
              onClick={() => setViewSize("small")}
              className={`p-1.5 md:p-2 rounded-lg transition-all ${viewSize === "small" ? "bg-white text-indigo-600 shadow-sm" : "text-slate-500 hover:text-slate-900"}`}
              title="Small Grid"
            >
              <HiOutlineViewGrid size={14} />
            </button>
            <button
              onClick={() => setViewSize("list")}
              className={`p-1.5 md:p-2 rounded-lg transition-all ${viewSize === "list" ? "bg-white text-indigo-600 shadow-sm" : "text-slate-500 hover:text-slate-900"}`}
              title="List View"
            >
              <HiOutlineViewList size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Folders Section */}
      {folders.length > 0 && (
        <section className="mb-12">
          <h2 className="text-slate-900 text-lg font-bold mb-6">Shared Folders</h2>
          <div key={`grid-${sortOrder}-${sortBy}`} className={`grid gap-6 ${viewSize === "list" ? "grid-cols-1" : "grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8"}`}>
            {folders.map((folder, index) => (
              <FolderCard
                key={folder.id}
                folder={folder}
                index={index}
                view={viewSize === "list" ? "list" : "grid"}
                isBig={viewSize === "big"}
                onClick={() => handleFolderClick(folder)}
                isSelected={selectedItems.includes(`folder:${folder.id}`)}
                onToggleSelect={() => handleToggleSelect(folder, "folder")}
              />
            ))}
          </div>
        </section>
      )}

      {/* Files Section */}
      <section>
        <h2 className="text-slate-900 text-lg font-bold mb-6">Shared Files</h2>
        {viewSize === "list" ? (
          <div className="overflow-x-auto fancy-scrollbar">
            <table className="w-full border-separate border-spacing-y-3">
              <thead>
                <tr className="text-slate-500 text-[10px] font-bold uppercase tracking-[0.2em]">
                  <th className="px-6 py-4 text-left font-bold w-12">
                    <div className="w-5 h-5 rounded-md border-2 border-slate-200" />
                  </th>
                  <th className="px-6 py-4 text-left font-bold">Name</th>
                  <th className="hidden md:table-cell px-6 py-4 text-left font-bold">Shared By</th>
                  <th className="hidden md:table-cell px-6 py-4 text-left font-bold">Status</th>
                  <th className="px-6 py-4 text-right font-bold">Action</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {files.map((file) => (
                    <FileCard
                      key={file.id}
                      file={{
                        ...file,
                        ownerName: file.sharedBy?.fullName || "Test"
                      }}
                      view={viewSize === "list" ? "list" : "grid"}
                      isBig={viewSize === "big"}
                      isSelected={selectedItems.includes(`file:${file.id}`)}
                      onToggleSelect={() => handleToggleSelect(file, "file")}
                      onView={() => setViewingFile(file)}
                      onDownloadClick={setDownloadItem}
                    />
                  ))}
                </AnimatePresence>
                {files.length === 0 && !loadingData && folders.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-20 text-center">
                      <div className="flex flex-col items-center gap-4 text-slate-600">
                        <HiOutlineEmojiSad size={64} className="opacity-20" />
                        <p className="font-bold text-lg">No shared items found</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div key={`grid-${sortOrder}-${sortBy}`} className={`grid gap-6 ${viewSize === "list" ? "grid-cols-1" : "grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8"}`}>
            <AnimatePresence>
              {files.map((file) => (
                <FileCard
                  key={file.id}
                  file={{
                    ...file,
                    displayName: file.name,
                    ownerName: file.sharedBy?.fullName || "Test"
                  }}
                  view="grid"
                  isBig={viewSize === "big"}
                  isSelected={selectedItems.includes(`file:${file.id}`)}
                  onToggleSelect={() => handleToggleSelect(file, "file")}
                  onView={() => setViewingFile(file)}
                  onDownloadClick={setDownloadItem}
                />
              ))}
            </AnimatePresence>
          </div>
        )}
      </section>

      <ViewFileModal
        file={viewingFile}
        isOpen={!!viewingFile}
        onClose={() => setViewingFile(null)}
      />

      <RenameModal isOpen={!!renamingItem} onClose={() => setRenamingItem(null)} currentName={renamingItem?.name} onRename={handleRenameSubmit} />
      <ShareModal isOpen={!!sharingItem} onClose={() => setSharingItem(null)} resource={sharingItem} onShare={handleShareSubmit} />
      <DownloadConfirmModal isOpen={!!downloadItem} onClose={() => setDownloadItem(null)} file={downloadItem} onConfirm={() => window.dispatchEvent(new CustomEvent('triggerDownload', { detail: { fileId: downloadItem.id } }))} />
    </div>
  );
}
